import * as Sentry from '@sentry/react';
import { createMatchSelector, push } from 'connected-react-router';
import noop from 'lodash/noop';
import type { match } from 'react-router';
import { FLUSH } from 'redux-persist';
import type { ThunkAction } from 'redux-thunk';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { updateDeliveryAddress, validateDeliveryAddress } from 'src/app/client/models/deliveryAddress';
import {
  AccountPageAddAddress,
  checkoutAddAddress,
  login,
  medications,
  resetPassword,
  validateAddress,
} from 'src/app/router/paths';
import * as actionTypes from 'src/app/state/actions/types';
import { getCurrentAccountId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import { COOKIE_IS_LOGGED_IN } from 'src/app/storage/cookieKeys';
import { clearCookie } from 'src/app/storage/cookies';
import { EXCHANGE_TOKEN_LOCAL_STORAGE_KEY, MFA_AUTH_CHANNEL } from 'src/app/storage/localStorageKeys';
import { API_ERROR_CODES } from 'src/consts';

export * from 'src/app/state/actions/checkout';
export * from 'src/app/state/actions/order';
export * from 'src/app/state/actions/leadForm';
export * from 'src/app/state/actions/onBoardingActions';
export * from 'src/app/state/actions/onBoardingFlow';
export * from 'src/app/state/actions/account';
export * from 'src/app/state/actions/location';
export * from 'src/app/state/actions/medication';
export * from 'src/app/state/actions/transferMedications';
export * from 'src/app/state/actions/insurance';
export * from 'src/app/state/actions/uiActions';
export * from 'src/app/state/actions/modal';
export * from 'src/app/state/actions/routerLocation';

export function clientRegister(client) {
  return {
    type: actionTypes.CLIENT_REGISTER,
    client: client,
  };
}

export function clientStart() {
  return {
    type: actionTypes.CLIENT_START,
  };
}

/* AUTH */
export function authSetRedirect(path) {
  return {
    type: actionTypes.AUTH_SET_REDIRECT,
    path: path,
  };
}

export function authClearRedirect() {
  return {
    type: actionTypes.AUTH_CLEAR_REDIRECT,
  };
}

export function authRedirect() {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const redirect = state?.redirect?.path || medications.toPath();

    dispatch(push(redirect));
    dispatch(authClearRedirect());
  };
}

type AuthParams = {
  encodedUserId?: string;
  token?: string;
};

export function authLogOut() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { router } = state;

    const queryParameters = router?.location?.search;

    // Clear token + code query parameters to prevent infinite loop when user signs in via capOs
    const newQueryParameters = new URLSearchParams(queryParameters);
    if (newQueryParameters.has('token')) newQueryParameters.delete('token');
    if (newQueryParameters.has('code')) newQueryParameters.delete('code');

    clearCookie(COOKIE_IS_LOGGED_IN);

    dispatch(sessionClearCurrentUserId());
    window.localStorage.removeItem(MFA_AUTH_CHANNEL);

    console.log(
      'Removing exchange token and before that its value is:',
      window?.localStorage?.getItem(EXCHANGE_TOKEN_LOCAL_STORAGE_KEY)
    );

    window?.localStorage?.removeItem(EXCHANGE_TOKEN_LOCAL_STORAGE_KEY);

    dispatch({ type: FLUSH, result: noop });

    /**
     * We want to prevent redirecting a logged-in user when they visit the password reset page.
     * It is possible for a user to be logged in on one device, reset the password on another, but return to the device
     * where the user is still logged in to complete the password reset flow. Before this effect, the user was
     * confusingly redirected to the login page.
     */
    const pwResetMacth: match<AuthParams> | null = createMatchSelector(resetPassword.route)(state);
    const isPasswordResetPage = !!pwResetMacth?.params?.encodedUserId && !!pwResetMacth?.params?.token;
    if (isPasswordResetPage) return;

    // reload the page which should cancel all current requests and clear our cache
    if (newQueryParameters.toString().length !== 0) {
      dispatch(push(`${login.route}?${newQueryParameters}`));
      return;
    }

    dispatch(push(login.route));
    return;
  };
}

/**
 * This action tells our Segment middleware to log a
 * PAGE event
 *
 * @TODO consider exposing an arg to explicity specify
 * a page name.
 *
 * @TODO expose `meta` property on payload
 */
export function segmentAnalyticsPage() {
  return {
    type: actionTypes.SEGMENT_PAGE,
  };
}

/**
 * @deprecated Record a click event. This is an old-style "Clicked Element" event.
 */
export function segmentAnalyticsClick(name: string) {
  return {
    type: actionTypes.SEGMENT_CLICK,
    name,
  };
}

/**
 * This action tells our Segment middleware to log a
 * TRACK event
 *
 * @param {string} name
 * @param {Object?} extra properties
 *
 * @TODO rename segmentAnalyticsEvent -> segmentAnalyticsTrack
 * - This is more reflective of Segment's TRACK event name
 */
export function segmentAnalyticsEvent(name: string, extra: object | null = {}) {
  return {
    type: actionTypes.SEGMENT_TRACK,
    name,
    extra,
  };
}

export function segmentAnalyticsIdentify(accountId: number | string) {
  return {
    type: actionTypes.SEGMENT_IDENTIFY,
    accountId: `${accountId}`,
  };
}

export type SegmentTrackType = (event: UnifiedEvent, payload?: any) => ThunkAction<Promise<void>, RootState, undefined>;
/**
 * # segmentTrack
 *
 * This is a thunk for dispatching new, UNIFIED analytics events,
 * where `useSegmentTrack` & `useLazySegmentTrack` custom react hooks
 * aren't available.
 * - ex. inside a `withFormik` higher order component.
 *
 * @usage
 *
 * ```typescript
 * import { segmentTrack } from 'src/app/state/actions'
 * import { UNIFIED, UnifiedEvent } from 'src/app/analytics/analyticsEvents';
 *
 * function mapDispatchToProps = dispatch => ({
 *   dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload))
 * })
 *
 * // condensed
 * const enhanceWithFormik = withFormik({
 *   handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
 *     const { dispatchLogin, dispatchSegmentTrack } = props;
 *     const { email, password } = values;
 *
 *     setSubmitting(true);
 *
 *     try {
 *       await dispatchLogin(email, password);
 *       dispatchSegmentTrack(UNIFIED.loginSuccessful, null);
 *     } catch (error) {
 *       dispatchSegmentTrack(UNIFIED.loginFailed, {
 *         error_code: error.response?.status,
 *         error_summary: error.response?.statusText,
 *       });
 *       setErrors(extractValidationErrors(error));
 *       setSubmitting(false);
 *     }
 *   },
 * });
 * ```
 *
 * @param {*} payload an object whose keys should match the UNIFIED event's
 *                    properties array
 */
export const segmentTrack: SegmentTrackType = function (event: UnifiedEvent, payload: any = {}) {
  const { name, properties } = event;
  const missingProps: string[] = [];
  properties?.forEach(key => {
    if (!(key in payload)) {
      missingProps.push(key);
    }
  });

  if (missingProps.length > 0) {
    const missingPropsStr = missingProps.map(key => `• ${key}`).join('\n');
    console.error(`dispatch - segmentTrack()\nMissing or misspelled keys in payload:\n ${name}:\n ${missingPropsStr}`);
  }

  return async (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.SEGMENT_TRACK,
      name,
      extra: payload,
    });
  };
};

/**
 * SESSION
 */

/**
 * sessionSetCurrentUser
 * - called in @see /state/actions/account by
 *   - accountSetup()
 *   - loginUser()
 */
export function sessionSetCurrentUser(user: any) {
  return (dispatch: Dispatch) => {
    const currentUserId = user?.id;
    const currentUserEmail = user?.attributes?.email;
    const currentAccountId = user?.relationships?.account?.data?.id;

    dispatch(sessionSetCurrentUserId(currentUserId, currentAccountId));

    Sentry.setUser({
      id: currentUserId,
      email: currentUserEmail,
    });
  };
}

export function sessionSetCurrentUserFromV3User(user: any) {
  return (dispatch: Dispatch) => {
    dispatch(sessionSetCurrentUserId(user.userId, user.accountId));

    Sentry.setUser({
      id: user.userId,
      email: user.email,
    });
  };
}

// GetStarted
export function sessionSetCurrentUserFromAccount(account) {
  return (dispatch: Dispatch) => {
    const { accountId, email, userId } = account;

    dispatch(sessionSetCurrentUserId(userId, accountId));

    Sentry.setUser({
      id: userId,
      email,
    });
  };
}

/**
 * sessionSetCurrentUserId
 * - redux-segment's reducer will read from this action payload, and
 * @see /state/segment.js
 */
export function sessionSetCurrentUserId(currentUserId: any, currentAccountId: any) {
  if (!currentUserId || !currentAccountId) {
    throw new Error(`Invalid \`currentUserId\` ${currentUserId} ${currentAccountId}`);
  }

  return {
    type: actionTypes.SESSION_SET_CURRENT_USER_ID,
    currentUserId,
    currentAccountId,
  };
}

export function sessionClearCurrentUserId() {
  return {
    type: actionTypes.SESSION_CLEAR_CURRENT_USER_ID,
  };
}

/**
 * VIEW
 */

export function viewSetOrders(orderIds) {
  return {
    type: actionTypes.VIEW_SET_ORDERS,
    orderIds: Array.isArray(orderIds) ? orderIds : [orderIds],
  };
}

export function viewClearOrders() {
  return {
    type: actionTypes.VIEW_CLEAR_ORDERS,
  };
}

/**
 * CART
 */

export function cartAddItems(items) {
  return {
    type: actionTypes.CART_ADD_ITEMS,
    items: Array.isArray(items) ? items : [items],
  };
}

export function cartUpdateItem({
  otcItemId,
  quantity,
  price,
}: {
  otcItemId: string;
  quantity: number;
  price?: number;
}) {
  return {
    type: actionTypes.CART_UPDATE_ITEMS,
    otcItemId,
    quantity,
    price,
  };
}

export function cartRemoveItems(itemsOrItem, cartId?: string) {
  const type = actionTypes.CART_REMOVE_ITEMS;
  const items = Array.isArray(itemsOrItem) ? itemsOrItem : [itemsOrItem];

  return cartId ? { type, items, cartId } : { type, items };
}

export function cartClear() {
  return {
    type: actionTypes.CART_CLEAR,
  };
}

export function checkoutSetOrder(orderId) {
  return {
    type: actionTypes.CHECKOUT_SET_ORDER,
    orderId,
  };
}

export const checkoutRequiresAccount = () => ({
  type: actionTypes.CHECKOUT_REQUIRES_ACCOUNT,
});

export function checkoutSetDeliveryAddress(deliveryAddress) {
  return {
    type: actionTypes.CHECKOUT_SET_DELIVERY_ADDRESS,
    deliveryAddress,
  };
}

export function checkoutSetPaymentCardId(paymentCardId) {
  return {
    type: actionTypes.CHECKOUT_SET_PAYMENT_CARD_ID,
    paymentCardId,
  };
}

export function checkoutSetDeliveryWindow(deliveryWindow) {
  return {
    type: actionTypes.CHECKOUT_SET_DELIVERY_WINDOW,
    deliveryWindow,
  };
}

export function checkoutSetCartId(cartId) {
  return {
    type: actionTypes.CHECKOUT_SET_CART_ID,
    cartId,
  };
}

export function clearResource(resourceType) {
  return {
    type: actionTypes.CLEAR_RESOURCE,
    resourceType,
  };
}

export function setPageAlert(pageAlertType) {
  return {
    type: actionTypes.PAGE_ALERT,
    pageAlertType,
  };
}

export function setSnackBarActive(snackBarType, snackBarState) {
  return {
    type: actionTypes.SNACK_BAR,
    snackBarState,
    snackBarType,
  };
}

export const editAddress = address => async (dispatch: Dispatch) => {
  await dispatch(setEditAddressState(address));
  await dispatch(push(AccountPageAddAddress.toPath()));
};

export const editDeliveryAddress = (address, cartId) => async (dispatch: Dispatch) => {
  await dispatch(setEditAddressState(address));
  await dispatch(push(checkoutAddAddress.toPath({ cartId })));
};

export function setEditAddressState(address) {
  return {
    type: actionTypes.SET_EDIT_ADDRESS_STATE,
    address,
  };
}

export function clearEditAddressState() {
  return {
    type: actionTypes.CLEAR_EDIT_ADDRESS_STATE,
  };
}

export function validateAddressAction(formValues, successAction, pushEditRoute, prevId) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);

    try {
      let result: {
        data?: {
          data: {
            attributes: any;
            id: string;
            relationships: any;
            type: string;
          };
        };
        status?: number;
      } = {};

      // preId means we are editing an address instead of adding,
      // is_valid only exists after suggestion flow
      if (prevId && formValues.hasOwnProperty('is_valid')) {
        result = await updateDeliveryAddress(client, prevId, { accountId, attributes: formValues });
        const deliveryAddress = result.data.data;
        successAction(deliveryAddress);
        return;
      } else {
        result = await validateDeliveryAddress(client, { formValues, accountId });
      }
      // Note: This will throw if result.data.data.attributes DNE.
      const { city, is_doorman, state, street, street_two, zip_code, notes } = result?.data?.data?.attributes ?? null;

      const id = result?.data?.data?.id;

      if (result.status === 201) {
        // successfully saved
        const deliveryAddress = result.data.data;
        successAction(deliveryAddress);
        dispatch(clearAddressValidationAction());
      } else if (result.status === 200) {
        // suggested address edit
        dispatch(
          setValidateAddressSuggestionsAction({
            invalidAddress: {
              ...formValues,
              city,
              state,
            },
            suggestedAddress: {
              id,
              city,
              is_doorman,
              state,
              street,
              street_two,
              zip_code,
              notes,
            },
            successAction: successAction,
            pushEditRoute: pushEditRoute,
          } as any)
        );
        dispatch(push(validateAddress.toPath()));
      }
      return result;
    } catch (error: any) {
      const isAddressNotFound = error.response?.data?.errors?.some((item: { code?: string }) => {
        return item?.code === API_ERROR_CODES.delivery_address_validation_error_not_found;
      });

      if (isAddressNotFound) {
        dispatch(
          setValidateAddressSuggestionsAction({
            invalidAddress: {
              ...formValues,
              city: error?.response?.data?.errors?.[0]?.detail ?? null,
              state: error?.response?.data?.errors?.[1]?.detail ?? null,
            },
            suggestedAddress: null,
            successAction: successAction,
            pushEditRoute: pushEditRoute,
          } as any)
        );
        dispatch(push(validateAddress.toPath()));
      }

      throw error;
    }
  };
}

/* setValidateAddressSuggestionsAction
 *
 * userValidatedAddress: Boolean
 * invalidAddress: Object
 * suggestedAddresses: Object
 *
 * successAction: Function --> this is a dispatched action that fires when an address is successfully added
 * It should be used to send a user to the next route in their current worklow
 * as of 8/20/2019 the current workflows are either checkout or account management
 *
 * pushEditRoute: Function --> dispatches a route change that takes the user to the appropriate address edit screen
 *
 */

export function setValidateAddressSuggestionsAction({
  userValidatedAddress,
  invalidAddress,
  suggestedAddress,
  successAction,
  pushEditRoute,
}) {
  return {
    type: actionTypes.SET_VALIDATE_ADDRESS_SUGGESTIONS,
    data: { userValidatedAddress, invalidAddress, suggestedAddress, successAction, pushEditRoute },
  };
}

export function setValidateAddressFlagAction({ userValidatedAddress }) {
  return {
    type: actionTypes.SET_VALIDATE_ADDRESS_FLAG,
    data: { userValidatedAddress },
  };
}

export function clearAddressValidationAction() {
  return {
    type: actionTypes.CLEAR_ADDRESS_VALIDATION,
  };
}
